import React, { useRef, useState } from "react";
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Divider,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
  formatDate,
  getColorShades,
  toTitleCase,
} from "../../../../../utils/utils";
import useIsOverflow from "../../../../../components/useIsOverflow";
import FileActionsMenu from "./FilesActionMenu";
import { DynamicIcon } from "../../../../../components/CustomIcons";
import ConfirmationDialog from "../../../../../components/ConfirmationDialog";
import FileStatus from "./FileStatus";
import { getFileIconImage } from "../../../../../utils/fileUtils";

const useStyles = makeStyles()((theme) => ({
  gridViewFilesContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "start",
    flexWrap: "wrap",
    rowGap: 16,
    columnGap: 20,
    [theme.breakpoints.down("sm")]: {
      rowGap: 12,
      columnGap: 16,
    },
  },
  gridViewFileItem: {
    width: 250,
    borderRadius: 16,
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  disabledGridViewFileItem: {
    width: 146,
    borderRadius: 16,
    backgroundColor: theme.palette.background.light,
  },
  countChip: {
    borderRadius: 4,
    fontWeight: 500,
  },
}));

function GridView({
  filteredCurrentDirectory,
  handleSelectFile,
  handleDeleteFile,
  handleUpdateFile,
  handleSelectDirectory,
}) {
  const { classes } = useStyles();
  const { directories, files } = filteredCurrentDirectory;
  const isRootDirectory = !filteredCurrentDirectory.current_path;

  return (
    <Box>
      {isRootDirectory && directories.length > 0 && (
        <Box mb={2.5} display={"flex"} alignItems={"center"} gap={1}>
          <Typography variant="subtitle2">Directory Folders</Typography>
          <Chip
            label={directories.length}
            size="small"
            className={classes.countChip}
          />
        </Box>
      )}
      <Box className={classes.gridViewFilesContainer}>
        {directories.map((directory, index) => {
          return (
            <GridViewItemDirectory
              key={index}
              directory={directory}
              handleSelectDirectory={handleSelectDirectory}
            />
          );
        })}
      </Box>
      {isRootDirectory && files.length > 0 && (
        <>
          <Divider sx={{ marginY: 3, borderStyle: "dashed" }} />
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Typography variant="subtitle2">All Files</Typography>
            <Chip
              label={files.length}
              size="small"
              className={classes.countChip}
            />
          </Box>
        </>
      )}
      <Box mb={2.5} />
      <Box className={classes.gridViewFilesContainer}>
        {files.map((file, index) => {
          return (
            <GridViewItemFile
              key={index}
              file={file}
              handleSelectFile={handleSelectFile}
              handleDeleteFile={handleDeleteFile}
              handleUpdateFile={handleUpdateFile}
            />
          );
        })}
      </Box>
    </Box>
  );
}

const FILE_STATUSES = ["processed", "completed", "uploaded"];

export const notSupportedBrowserFiles = ["xls", "xlsx", "docx"];

function GridViewItemDirectory({ directory, handleSelectDirectory }) {
  const { name } = directory;
  return (
    <Box
      width={146}
      position={"relative"}
      sx={{ cursor: "pointer" }}
      onClick={() => handleSelectDirectory(directory["path"])}>
      <img src={"/images/file_icons/folder.svg"} alt="folder" width={"100%"} />
      <Tooltip title={name} arrow>
        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            left: 12,
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
          {name}
        </Typography>
      </Tooltip>
    </Box>
  );
}

function GridViewItemFile({
  file,
  handleSelectFile,
  handleDeleteFile,
  handleUpdateFile,
}) {
  const { classes } = useStyles();
  const { name, file_type, file_url, created_at, status, directory } = file;
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const handleOpenFileInNewTab = () => {
    if (notSupportedBrowserFiles.includes(file_type)) {
      window.open(file_url, "_blank", "noopener,noreferrer");
    } else {
      handleSelectFile(file, true);
    }
  };

  const handleClickFile = () => {
    if (file_type === "docx") {
      setIsConfirmationDialogOpen(true);
    } else {
      handleSelectFile(file, false);
    }
  };

  const handleClickYes = () => {
    handleSelectFile(file);
    setIsConfirmationDialogOpen(false);
  };

  return (
    <>
      <Card elevation={0} className={classes.gridViewFileItem}>
        <CardActionArea
          sx={{
            height: "100%",
          }}
          disableRipple={true}
          disableTouchRipple={true}
          onClick={handleClickFile}>
          <CardContent sx={{ height: "100%", padding: 1.5 }}>
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"start"}
              mb={1.5}>
              <img
                src={getFileIconImage(file_type)}
                alt="file_icon"
                width={27}
                height={30}
              />
              <FileStatus status={status} />
            </Box>
            <Tooltip title={name} arrow>
              <Typography
                variant="body2"
                fontWeight={400}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                {name}
              </Typography>
            </Tooltip>
            <Tooltip title={directory} arrow>
              <Typography
                variant="caption"
                color={"textSecondary"}
                m
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                {directory}
              </Typography>
            </Tooltip>
            <Box
              mt={1}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}>
              <Typography variant="caption" color={"textSecondary"}>
                {formatDate(created_at)}
              </Typography>
              <Box onClick={(e) => e.stopPropagation()}>
                <FileActionsMenu
                  file={file}
                  handleDeleteFile={handleDeleteFile}
                  handleUpdateFile={handleUpdateFile}
                  handleOpenFileInNewTab={handleOpenFileInNewTab}
                />
              </Box>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
      {isConfirmationDialogOpen && (
        <ConfirmationDialog
          title={"Download and open file?"}
          content={
            "This file cannot be opened in the browser. Do you want to download and open in your system?"
          }
          onNo={() => setIsConfirmationDialogOpen(false)}
          onYes={handleClickYes}
        />
      )}
    </>
  );
}

export function OverflowingText({ text }) {
  const ref = useRef();
  const isOverflow = useIsOverflow(ref);
  return (
    <Tooltip title={text} arrow disableHoverListener={!isOverflow}>
      <Typography
        ref={ref}
        variant="body2"
        fontWeight={400}
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
        {text}
      </Typography>
    </Tooltip>
  );
}

export function DomainChip({ domain, isFileCompleted = true }) {
  const { icon, color } = domain;
  const domainName = domain["domain"];
  const { lightest, main, dark } = getColorShades(color);
  return (
    <Chip
      size="small"
      label={toTitleCase(domainName)}
      icon={
        <DynamicIcon
          iconName={icon}
          sx={{ color: main, fontSize: 18, marginLeft: 0.5 }}
        />
      }
      sx={{
        color: dark,
        background: lightest,
        height: 24,
        "& .MuiChip-label": {
          fontSize: "0.75rem",
          padding: "0 8px",
        },
      }}
      disabled={!isFileCompleted}
    />
  );
}

export default GridView;
